// CepageExplorePage — dedicated public page for a single grape variety,
// mirroring AppellationExplorePage. Fetches meta from /api/public/cepages
// and renders the public Atlas blocks (already translated + polling via
// AtlasBlocksSection with entity_type=cepage).
//
// Route: /cepage/:slug
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Grape } from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import AtlasBlocksSection from "@/components/AtlasBlocksSection";
import ReadingProgressBar from "@/components/ReadingProgressBar";
import { useLang } from "@/lib/i18n";
import { apiFetch } from "@/lib/api";

const COLOR_DOT = {
  Rouge: "bg-[#8B1E3F]",
  Blanc: "bg-[#E8D4A0]",
  Rosé: "bg-[#F4A6B5]",
};

const CepageExplorePage = () => {
  const { slug } = useParams();
  const lang = useLang();
  const [meta, setMeta] = useState(null);
  const [emblematicApps, setEmblematicApps] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { ok, data } = await apiFetch("/api/public/cepages?limit=500");
      if (!cancelled && ok) {
        const item = (data?.items || []).find((it) => it.slug === slug);
        setMeta(item || { slug, name: slug, color: "", origin: "" });
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Phase 2 GEO — pull the appellations list once and derive up to 6 that
  // list the current cépage among their authorized grapes. Runs after
  // `meta` is set so we can filter on the display name (case-insensitive,
  // substring match to catch variants like "Cabernet Sauvignon" vs
  // "Cabernet-Sauvignon").
  useEffect(() => {
    if (!meta?.name) return;
    let cancelled = false;
    const needle = meta.name.toLowerCase();
    (async () => {
      const { ok, data } = await apiFetch("/api/public/appellations?limit=500");
      if (cancelled || !ok) return;
      const items = data?.items || [];
      const matches = items
        .filter((a) => Array.isArray(a.grapes) && a.grapes.some((g) => String(g).toLowerCase().includes(needle)))
        .slice(0, 6);
      setEmblematicApps(matches);
    })();
    return () => { cancelled = true; };
  }, [meta?.name]);

  const displayName = useMemo(() => meta?.name || slug, [meta, slug]);
  const color = meta?.color || "";
  const dot = COLOR_DOT[color] || "bg-[color:var(--gv-ink)]/30";

  return (
    <Layout testId={`cepage-${slug}`}>
      <SEO
        title={`${displayName} — Cépage · geoVINUM`}
        description={meta ? `Découvrez le cépage ${displayName}${meta.origin ? `, originaire de ${meta.origin}` : ""}. Arômes, terroirs, vinifications et accords.` : ""}
      />
      <ReadingProgressBar targetSelector={`[data-testid="cepage-${slug}"] article`} />

      <section className="bg-[color:var(--gv-mist)] pt-10 pb-8">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <Breadcrumb
            items={[
              { to: "/", label: "Accueil" },
              { to: "/blog", label: "Explorer" },
              { label: displayName },
            ]}
          />
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-[13px] text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)] mt-4 mb-6" data-testid="cepage-back">
            <ArrowLeft size={14} /> Tous les cépages
          </Link>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-pink)] mb-3 inline-flex items-center gap-2">
            <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />
            Cépage{color && <> · {color}</>}{meta?.origin && <> · {meta.origin}</>}
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-[color:var(--gv-ink)] mb-5" data-testid="cepage-title">
            {displayName}
          </h1>
          <div className="flex flex-wrap gap-3">
            <a
              href={`https://app.geovinum.io/cepages/${slug}`}
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
              data-testid="cepage-open-app"
            >
              Ouvrir dans l&apos;app <ExternalLink size={14} />
            </a>
            <Link to="/blog?cat=cepage" className="btn-secondary" data-testid="cepage-back-index">
              <Grape size={14} /> Tous les cépages
            </Link>
          </div>
        </div>
      </section>

      <article className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          {/* Phase 2 SEO/GEO — Plain-HTML relations strip so LLM crawlers
              read the varietal graph (regions, aromatic profile, emblematic
              appellations) without touching JS. */}
          {meta && (
            <nav
              aria-label="Relations"
              className="mb-10 pb-6 border-b border-[color:var(--gv-line)] text-[13.5px] text-[color:var(--gv-ink)]/85 leading-relaxed"
              data-testid="cepage-relations"
            >
              <p>
                <strong>{displayName}</strong> est un cépage
                {color ? <> {color.toLowerCase()}</> : null}
                {meta.origin ? <> originaire de <Link to={`/blog?q=${encodeURIComponent(meta.origin)}`} className="text-[color:var(--gv-pink)] hover:underline">{meta.origin}</Link></> : null}
                .
                {Array.isArray(meta.regions) && meta.regions.length > 0 && (
                  <> Cultivé notamment en {meta.regions.slice(0, 6).map((r, i) => (
                    <span key={r}>
                      {i > 0 ? ", " : ""}
                      <Link to={`/blog?q=${encodeURIComponent(r)}`} className="text-[color:var(--gv-pink)] hover:underline">{r}</Link>
                    </span>
                  ))}.</>
                )}
              </p>
              {meta.aromatic_profile && (
                <p className="mt-2">
                  <strong>Profil aromatique</strong> — {meta.aromatic_profile}
                </p>
              )}
              {emblematicApps.length > 0 && (
                <p className="mt-2">
                  <strong>Appellations emblématiques</strong> —{" "}
                  {emblematicApps.map((a, i) => (
                    <span key={a.slug}>
                      {i > 0 ? ", " : ""}
                      <Link to={`/explore/${a.slug}`} className="text-[color:var(--gv-pink)] hover:underline">{a.name}</Link>
                      {a.region ? <span className="text-[color:var(--gv-ink)]/55"> ({a.region})</span> : null}
                    </span>
                  ))}.
                </p>
              )}
              <p className="mt-2 text-[color:var(--gv-ink)]/65">
                Aller plus loin —{" "}
                <Link to={`/shop?q=${encodeURIComponent(displayName)}`} className="text-[color:var(--gv-pink)] hover:underline">Cartes {displayName}</Link>
                {" · "}
                <Link to={`/app#academy`} className="text-[color:var(--gv-pink)] hover:underline">Apprendre {displayName} sur geoVINUM app</Link>
                {" · "}
                <Link to={`/app#ai`} className="text-[color:var(--gv-pink)] hover:underline">Poser une question à geoVINUM AI</Link>
              </p>
            </nav>
          )}

          <AtlasBlocksSection
            cepage={displayName}
            lang={lang}
          />
        </div>
      </article>
    </Layout>
  );
};

export default CepageExplorePage;
